import React from "react";
import { MdPhoneEnabled, MdMailOutline } from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";

const raleway = { fontFamily: "'Geist', sans-serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };

const CHANNELS = [
  {
    title: "Phone Support",
    description: "Speak to a customer care officer directly for urgent help.",
    contact: "+234 81 ******",
    contactHref: "tel:+2349120363787",
    note: "Open now · Mon–Fri 8:am – 6pm",
    cta: "Call now",
    Icon: MdPhoneEnabled,
  },
  {
    title: "Email Support",
    description:
      "For detailed enquiries and anything that needs documents attached.",
    contact: "customer.service@zikoramfb.com",
    contactHref: "mailto:customer.service@zikoramfb.com",
    note: "Typical reply within 24 hours",
    cta: "Send an Email",
    Icon: MdMailOutline,
  },
  {
    title: "Whatsapp Support",
    description:
      "Chat with us for quick questions, balance issues, and guidance.",
    contact: "+234 901 234 5678",
    contactHref: "https://wa.me/2349012345678",
    note: "Replies in ~1 hour · 7 days a week",
    cta: "Chat on Whatsapp",
    Icon: FaWhatsapp,
  },
];

const Card = ({ title, description, contact, contactHref, note, cta, Icon }) => (
  <div
    style={{
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: "20px",
      padding: "clamp(24px, 2.5vw, 36px)",
      background: "#FFFFFF",
      border: "1px solid #E5E9E6",
      borderRadius: "16px",
      boxSizing: "border-box",
    }}
  >
    {/* Round icon */}
    <div
      style={{
        width: "44px",
        height: "44px",
        borderRadius: "50%",
        background: "#EEF2EC",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#295B4A",
      }}
    >
      <Icon size={20} />
    </div>

    {/* Title + description */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <h3
        style={{
          ...raleway,
          fontWeight: 600,
          fontSize: "clamp(16px, 1.25vw, 18px)",
          lineHeight: 1.3,
          color: "#132A22",
          margin: 0,
        }}
      >
        {title}
      </h3>
      <p
        style={{
          ...raleway,
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: 1.6,
          color: "#6B7B70",
          margin: 0,
        }}
      >
        {description}
      </p>
    </div>

    {/* Contact + note */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        marginTop: "4px",
      }}
    >
      <a
        href={contactHref}
        target={contactHref?.startsWith("http") ? "_blank" : undefined}
        rel={
          contactHref?.startsWith("http") ? "noopener noreferrer" : undefined
        }
        style={{
          ...raleway,
          fontWeight: 600,
          fontSize: "15px",
          color: "#295B4A",
          textDecoration: "none",
          wordBreak: "break-word",
        }}
      >
        {contact}
      </a>
      <span
        style={{
          ...raleway,
          fontWeight: 400,
          fontSize: "13px",
          color: "#6B7B70",
        }}
      >
        {note}
      </span>
    </div>

    {/* CTA button */}
    <a
      href={contactHref}
      target={contactHref?.startsWith("http") ? "_blank" : undefined}
      rel={contactHref?.startsWith("http") ? "noopener noreferrer" : undefined}
      style={{
        ...raleway,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "flex-start",
        height: "40px",
        padding: "0 18px",
        marginTop: "auto",
        background: "#FFFFFF",
        border: "1px solid #D8DEDA",
        borderRadius: "10px",
        fontWeight: 500,
        fontSize: "14px",
        color: "#132A22",
        textDecoration: "none",
        whiteSpace: "nowrap",
        transition: "border-color 200ms ease, background 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#86C7A8";
        e.currentTarget.style.background = "#F6FAF7";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#D8DEDA";
        e.currentTarget.style.background = "#FFFFFF";
      }}
    >
      {cta}
    </a>
  </div>
);

const ContactChannels = () => {
  return (
    <>
      <style>{`
				@media (max-width: 900px) {
					.support-channels-row { flex-direction: column !important; }
				}
			`}</style>

      <section
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding:
            "clamp(56px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px) clamp(32px, 4vw, 60px)",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          {/* Section header */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: "clamp(12px, 1.4vw, 20px)",
              marginBottom: "clamp(32px, 4vw, 56px)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#295B4A",
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  ...geistMono,
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: 1,
                  letterSpacing: "0.1em",
                  color: "#295B4A",
                  textTransform: "uppercase",
                }}
              >
                Talk to us
              </span>
            </div>

            <h2
              style={{
                ...instrumentSerif,
                fontWeight: 400,
                fontSize: "clamp(28px, 3.9vw, 56px)",
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
                color: "#132A22",
                margin: 0,
                maxWidth: "min(720px, 100%)",
              }}
            >
              Reach the team however suits you.
            </h2>

            <p
              style={{
                ...raleway,
                fontWeight: 400,
                fontSize: "clamp(14px, 1.1vw, 16px)",
                lineHeight: 1.6,
                color: "#6B7B70",
                margin: 0,
                maxWidth: "min(520px, 100%)",
              }}
            >
              Real people ready to help you resolve things quickly.
            </p>
          </div>

          <div
            className="support-channels-row"
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "clamp(16px, 1.9vw, 28px)",
              alignItems: "stretch",
            }}
          >
            {CHANNELS?.map((c) => (
              <Card key={c?.title} {...c} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactChannels;
